import React, { useState, useEffect } from "react";
import useSheetDismiss from "../utils/useSheetDismiss";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import uuid from "react-uuid";
import TodoSettings from "./TodoSettings";
import {
  isSavingsEnabled,
  getAllAvailableCurrencies,
  getSavingsCurrencyConfig,
  saveSavingsCurrencyConfig,
} from "../service/SavingsService";
import {
  getExpenseCategory,
  addExpenseCategory,
  UpdateUser,
  fetchUsers,
  API_BASE,
  API_HEADERS,
} from "../service/APIService";
import {
  clearSharedExpenseCache,
  dispatchSharedExpenseUpdated,
} from "../service/sharedExpenseStore";
import "../styles/Setting.css";

const Setting = ({ loggedInUserEmail }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [shareWithEmail, setShareWithEmail] = useState("");
  const [success, setSuccess] = useState("");
  const [loggedinUserExpense, setLoggedinUserExpense] = useState([]);

  // Group connection state
  const [loadingConnection, setLoadingConnection] = useState(true);
  const [groupName, setGroupName] = useState("");
  const [inputGroupName, setInputGroupName] = useState("");
  const [connectedMembers, setConnectedMembers] = useState([]);
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);
  const [editGroupNameVal, setEditGroupNameVal] = useState("");
  const [showAddAnother, setShowAddAnother] = useState(false);

  // Themed Unlink Modal state
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const { closing: unlinkClosing, requestClose: closeUnlinkSheet } = useSheetDismiss(() => {
    setShowUnlinkModal(false);
    setUnlinkConfirmText("");
  });
  const [unlinkConfirmText, setUnlinkConfirmText] = useState("");

  // Savings Currency Configuration state
  const [savingsCurrencyCfg, setSavingsCurrencyCfg] = useState(() => getSavingsCurrencyConfig());
  const [showAllSavingsCurrencies, setShowAllSavingsCurrencies] = useState(false);
  const [customSavCode, setCustomSavCode] = useState("");
  const [customSavSym, setCustomSavSym] = useState("");

  useEffect(() => {
    const syncSavingsCurr = () => setSavingsCurrencyCfg(getSavingsCurrencyConfig());
    window.addEventListener("savings-currencies-updated", syncSavingsCurr);
    return () => window.removeEventListener("savings-currencies-updated", syncSavingsCurr);
  }, []);

  const handleToggleSavingsCurrency = (code) => {
    const isEnabled = savingsCurrencyCfg.enabledCodes.includes(code);
    if (isEnabled && savingsCurrencyCfg.enabledCodes.length <= 1) return;
    const nextEnabled = isEnabled
      ? savingsCurrencyCfg.enabledCodes.filter((c) => c !== code)
      : [...savingsCurrencyCfg.enabledCodes, code];
    const nextDefault = nextEnabled.includes(savingsCurrencyCfg.defaultCode)
      ? savingsCurrencyCfg.defaultCode
      : nextEnabled[0];
    setSavingsCurrencyCfg(
      saveSavingsCurrencyConfig({
        ...savingsCurrencyCfg,
        enabledCodes: nextEnabled,
        defaultCode: nextDefault,
      })
    );
  };

  const handleSetDefaultSavingsCurrency = (code) => {
    const nextEnabled = savingsCurrencyCfg.enabledCodes.includes(code)
      ? savingsCurrencyCfg.enabledCodes
      : [...savingsCurrencyCfg.enabledCodes, code];
    setSavingsCurrencyCfg(
      saveSavingsCurrencyConfig({
        ...savingsCurrencyCfg,
        enabledCodes: nextEnabled,
        defaultCode: code,
      })
    );
  };

  const handleAddCustomSavingsCurrency = (e) => {
    e.preventDefault();
    const code = customSavCode.trim().toUpperCase();
    const symbol = customSavSym.trim() || code;
    if (!code || code.length > 5) return;
    const existsInCustom = (savingsCurrencyCfg.customCurrencies || []).some((c) => c.code === code);
    const nextCustom = existsInCustom
      ? savingsCurrencyCfg.customCurrencies.map((c) => (c.code === code ? { code, symbol, label: code } : c))
      : [...(savingsCurrencyCfg.customCurrencies || []), { code, symbol, label: code }];
    const nextEnabled = savingsCurrencyCfg.enabledCodes.includes(code)
      ? savingsCurrencyCfg.enabledCodes
      : [...savingsCurrencyCfg.enabledCodes, code];
    setSavingsCurrencyCfg(
      saveSavingsCurrencyConfig({
        ...savingsCurrencyCfg,
        enabledCodes: nextEnabled,
        customCurrencies: nextCustom,
      })
    );
    setCustomSavCode("");
    setCustomSavSym("");
  };

  useEffect(() => {
    const sessionUser = JSON.parse(localStorage.getItem("loggedInUser"));
    const guestUser = JSON.parse(localStorage.getItem("guestUser"));

    const isRealUser = sessionUser && sessionUser.email && sessionUser.email !== "guest" && sessionUser.email.includes("@");

    if (isRealUser) {
      setIsLoggedIn(true);
      setEmail(sessionUser.email);
      setName(sessionUser.name || sessionUser.email.split("@")[0]);
      localStorage.setItem("loggedInUserEmail", sessionUser.email);
      localStorage.removeItem("guestUser");
      fetchCategories(sessionUser.email);
      getUsers(sessionUser.email);
    } else if (guestUser) {
      setIsLoggedIn(false);
      setEmail(guestUser.email || "guest");
      setName(guestUser.name || "Guest User");
      setLoadingConnection(false);
      loadGuestCategories();
    } else {
      setIsLoggedIn(false);
      setEmail("guest");
      setName("Guest User");
      setLoadingConnection(false);
      loadGuestCategories();
    }
  }, []);

  const loadGuestCategories = () => {
    const saved = JSON.parse(localStorage.getItem("guestExpenseCategories")) || [
      { id: "cat-1", category: "ENTERTAINMENT", type: "expense-category" },
      { id: "cat-2", category: "HEALTHCARE", type: "expense-category" },
      { id: "cat-3", category: "INVESTMENTS", type: "expense-category" },
    ];
    setCategories(saved);
  };

  const fetchCategories = async (userEmail) => {
    setLoading(true);
    try {
      const response = await getExpenseCategory(userEmail);
      setCategories(response.filter((item) => item.type === "expense-category"));
    } catch (err) {
      setError("Failed to fetch custom categories.");
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    setLoading(true);
    try {
      if (isLoggedIn) {
        await fetch(`${API_BASE}/removeitem/${id}`, {
          method: "DELETE",
          headers: API_HEADERS,
        });
        setCategories(categories.filter((cat) => cat.id !== id));
      } else {
        const updated = categories.filter((cat) => cat.id !== id);
        setCategories(updated);
        localStorage.setItem("guestExpenseCategories", JSON.stringify(updated));
      }
      setError("");
    } catch (err) {
      setError("Failed to delete category.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    if (e) e.preventDefault();
    if (!newCategory.trim()) {
      alert("Please enter a category name.");
      return;
    }

    const catName = newCategory.trim().toUpperCase();
    const categoryExists = categories.some(
      (cat) => cat.category.toLowerCase() === catName.toLowerCase()
    );

    if (categoryExists) {
      alert("Category already exists.");
      return;
    }

    const newCategoryPayload = {
      id: uuid(),
      email,
      date: new Date().toISOString(),
      category: catName,
      type: "expense-category",
    };

    setLoading(true);
    try {
      if (isLoggedIn) {
        await addExpenseCategory(newCategoryPayload);
        setCategories([...categories, newCategoryPayload]);
      } else {
        const updated = [...categories, newCategoryPayload];
        setCategories(updated);
        localStorage.setItem("guestExpenseCategories", JSON.stringify(updated));
      }
      setNewCategory("");
      setError("");
    } catch (err) {
      setError("Failed to add category.");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setSuccess("");

    const targetEmail = shareWithEmail.trim().toLowerCase();
    if (!targetEmail) {
      setError("Please provide an email address.");
      return;
    }

    const currentEmail = (email || "").trim().toLowerCase();
    if (currentEmail === targetEmail) {
      setError("You cannot share expenses with yourself.");
      return;
    }

    try {
      setLoading(true);
      const loggedinuser = await fetchUsers(currentEmail);
      const shareduser = await fetchUsers(targetEmail);

      if (!shareduser) {
        setError(`User "${targetEmail}" does not exist in the system. They must sign in at least once.`);
        return;
      }

      // Determine members list
      const existingShared = loggedinuser?.sharedWith
        ? loggedinuser.sharedWith.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)
        : [currentEmail];

      if (existingShared.includes(targetEmail)) {
        setError(`"${targetEmail}" is already linked in your group.`);
        return;
      }

      // One group per person for now: refuse if either side already belongs to another group.
      const myExistingPartners = existingShared.filter((e) => e !== currentEmail);
      if (myExistingPartners.length > 0) {
        setError(`You are already linked with ${myExistingPartners.join(", ")}. Remove that link before adding someone else.`);
        return;
      }

      const targetPartners = (shareduser.sharedWith || "")
        .split(",")
        .map((e) => e.trim().toLowerCase())
        .filter((e) => e && e !== targetEmail);
      if (targetPartners.length > 0) {
        setError(`This email is already shared with another group. You can not share with "${targetEmail}".`);
        return;
      }

      const combinedEmails = Array.from(new Set([...existingShared, targetEmail, currentEmail])).sort();
      const sharedWithString = combinedEmails.join(",");

      const myName = loggedinuser?.name || currentEmail.split("@")[0];
      const partnerName = shareduser?.name || targetEmail.split("@")[0];
      const finalGroupName = (
        inputGroupName.trim() ||
        groupName.trim() ||
        `${partnerName} ${myName}`
      ).slice(0, 15);

      const updatedLoggedInUser = {
        ...loggedinuser,
        sharedWith: sharedWithString,
        groupName: finalGroupName,
      };

      const updatedSharedUser = {
        ...shareduser,
        sharedWith: sharedWithString,
        groupName: finalGroupName,
      };

      await UpdateUser(updatedLoggedInUser);
      await UpdateUser(updatedSharedUser);

      setLoggedinUserExpense(sharedWithString);
      setGroupName(finalGroupName);
      setEditGroupNameVal(finalGroupName);
      setInputGroupName("");
      setShareWithEmail("");
      setShowAddAnother(false);
      setSuccess(`Successfully linked with ${partnerName} (${targetEmail}) in "${finalGroupName}"!`);

      // Clear local caches and notify across components
      clearSharedExpenseCache(currentEmail);
      clearSharedExpenseCache(targetEmail);
      dispatchSharedExpenseUpdated();

      // Refresh members
      await getUsers(currentEmail);
    } catch (err) {
      console.error("Failed to link user:", err);
      setError("Failed to link user. Please verify the email and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroupName = async (e) => {
    if (e) e.preventDefault();
    if (!editGroupNameVal.trim()) return;
    setLoading(true);
    try {
      const newName = editGroupNameVal.trim().slice(0, 15);
      setGroupName(newName);
      setIsEditingGroupName(false);

      const currentUser = await fetchUsers(email);
      if (currentUser) {
        await UpdateUser({ ...currentUser, groupName: newName });
      }

      if (connectedMembers && connectedMembers.length > 0) {
        for (const m of connectedMembers) {
          if (!m.isYou) {
            try {
              const otherUser = await fetchUsers(m.email);
              if (otherUser) {
                await UpdateUser({ ...otherUser, groupName: newName });
              }
            } catch (err) {
              console.warn("Could not update member group name:", err);
            }
          }
        }
      }
      clearSharedExpenseCache(email);
      dispatchSharedExpenseUpdated();
      setSuccess("Group name updated successfully!");
    } catch (err) {
      console.error("Failed to update group name:", err);
      setError("Failed to update group name.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUnlinkModal = () => {
    setUnlinkConfirmText("");
    setShowUnlinkModal(true);
  };

  const handleConfirmUnlink = async () => {
    if (unlinkConfirmText.trim().toLowerCase() !== "confirm") return;
    setLoading(true);
    try {
      const currentUser = await fetchUsers(email);
      if (currentUser) {
        await UpdateUser({ ...currentUser, sharedWith: "", groupName: "" });
      }

      for (const m of connectedMembers) {
        if (!m.isYou) {
          try {
            const otherUser = await fetchUsers(m.email);
            if (otherUser) {
              await UpdateUser({ ...otherUser, sharedWith: "", groupName: "" });
            }
          } catch (e) {
            console.warn("Could not unlink other user:", e);
          }
        }
      }

      // Clear cached shared expense data for current user and members
      clearSharedExpenseCache(email);
      for (const m of connectedMembers) {
        if (m.email) clearSharedExpenseCache(m.email);
      }
      dispatchSharedExpenseUpdated();

      setLoggedinUserExpense("");
      setConnectedMembers([]);
      setGroupName("");
      setInputGroupName("");
      setShareWithEmail("");
      setShowAddAnother(false);
      setShowUnlinkModal(false);
      setUnlinkConfirmText("");
      setSuccess("Successfully unlinked family sharing.");
    } catch (err) {
      console.error("Failed to unlink group:", err);
      setError("Failed to unlink family members.");
    } finally {
      setLoading(false);
    }
  };

  const getUsers = async (userEmail) => {
    setLoadingConnection(true);
    try {
      const response = await fetchUsers(userEmail);
      if (response && response.sharedWith && response.sharedWith.trim().length > 0) {
        setLoggedinUserExpense(response.sharedWith);
        const savedGroupName = response.groupName || "";
        setGroupName(savedGroupName);
        setEditGroupNameVal(savedGroupName);

        const rawList = response.sharedWith
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);
        const uniqueEmails = Array.from(new Set(rawList));

        const members = await Promise.all(
          uniqueEmails.map(async (mEmail) => {
            const isMe = mEmail === userEmail.toLowerCase();
            if (isMe) {
              return {
                email: mEmail,
                name: response.name || mEmail.split("@")[0],
                isYou: true,
              };
            }
            try {
              const u = await fetchUsers(mEmail);
              return {
                email: mEmail,
                name: u?.name || mEmail.split("@")[0],
                isYou: false,
              };
            } catch (e) {
              return {
                email: mEmail,
                name: mEmail.split("@")[0],
                isYou: false,
              };
            }
          })
        );

        setConnectedMembers(members);

        // Fallback default group name if none set
        if (!savedGroupName && members.length > 0) {
          const derivedName = members.map((m) => m.name).join(" ");
          setGroupName(derivedName);
          setEditGroupNameVal(derivedName);
        }
      } else {
        setLoggedinUserExpense("");
        setConnectedMembers([]);
        setGroupName("");
      }
    } catch (err) {
      console.error("Failed to fetch user shared data:", err);
    } finally {
      setLoadingConnection(false);
    }
  };

  return (
    <div className="settings-page-wrapper">
      {/* Profile & Account Banner */}
      <div className="settings-profile-card">
        <div className="settings-profile-info">
          <div className="settings-avatar">
            {name ? name.charAt(0).toUpperCase() : "U"}
          </div>
          <div className="settings-profile-text">
            <h3 className="settings-profile-name">{name}</h3>
            <p className="settings-profile-email">{email}</p>
            <span className="settings-profile-badge">
              {isLoggedIn ? "Member Account" : "Guest Mode"}
            </span>
          </div>
        </div>
        <Link to="/app1/profile" className="settings-profile-link-btn">
          <span>Profile</span>
          <i className="fi fi-rr-arrow-small-right"></i>
        </Link>
      </div>

      {/* Guest Mode Notice */}
      {!isLoggedIn && (
        <div className="settings-guest-banner">
          <h5>Unlock Family Sync & Cloud Backups</h5>
          <p>
            Log in to link and share expenses in real-time with family members and sync custom categories across all devices.
          </p>
          <Link
            to="/app1/profile"
            className="btn-coral-modern"
            style={{ padding: "10px 18px", fontSize: "13px", display: "inline-flex", width: "auto" }}
          >
            <i className="fi fi-rr-user"></i> Sign In
          </Link>
        </div>
      )}

      {/* Savings lives on its own page; only the listed accounts see the way in */}
      {isLoggedIn && isSavingsEnabled(email) && (
        <div className="settings-card settings-savings-card">
          <Link to="/app1/savings" className="settings-savings-link">
            <div className="settings-card-header" style={{ marginBottom: 0 }}>
              <div className="settings-card-icon">
                <i className="fi fi-rr-piggy-bank"></i>
              </div>
              <div style={{ flexGrow: 1 }}>
                <h4 className="settings-card-title">Savings</h4>
                <p className="settings-card-subtitle">
                  What every account is worth, and what it was worth before
                </p>
              </div>
              <i className="fi fi-rr-angle-small-right settings-savings-chevron"></i>
            </div>
          </Link>

          <div className="settings-savings-currencies">
            <div className="settings-savings-curr-head">
              <span>
                <b>Currencies (₹ / $)</b>
                <small>Toggle active currencies &amp; default view</small>
              </span>
              <button
                type="button"
                className="settings-savings-more-btn"
                onClick={() => setShowAllSavingsCurrencies((prev) => !prev)}
              >
                {showAllSavingsCurrencies ? "Less" : "+ More currencies"}
              </button>
            </div>

            <div className="settings-savings-curr-pills">
              {getAllAvailableCurrencies()
                .filter(
                  (c) =>
                    showAllSavingsCurrencies ||
                    savingsCurrencyCfg.enabledCodes.includes(c.code) ||
                    c.code === "INR" ||
                    c.code === "USD"
                )
                .map(({ code, symbol: sym }) => {
                  const enabled = savingsCurrencyCfg.enabledCodes.includes(code);
                  const isDefault = savingsCurrencyCfg.defaultCode === code;
                  return (
                    <div
                      key={code}
                      className={`settings-savings-curr-chip${enabled ? " is-enabled" : ""}`}
                    >
                      <button
                        type="button"
                        className="settings-savings-curr-toggle"
                        onClick={() => handleToggleSavingsCurrency(code)}
                        title={enabled ? "Tap to disable" : "Tap to enable"}
                      >
                        <span className="settings-savings-curr-sym">{sym}</span>
                        <span className="settings-savings-curr-code">{code}</span>
                        {enabled && <i className="fi fi-rr-check"></i>}
                      </button>
                      {enabled && (
                        <button
                          type="button"
                          className={`settings-savings-curr-def${isDefault ? " is-default" : ""}`}
                          onClick={() => handleSetDefaultSavingsCurrency(code)}
                          title="Set as default currency on Savings page"
                        >
                          {isDefault ? "★ Default" : "Set default"}
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>

            {showAllSavingsCurrencies && (
              <form className="savings-custom-currency-form is-in-settings" onSubmit={handleAddCustomSavingsCurrency}>
                <input
                  type="text"
                  placeholder="Code (e.g. CHF)"
                  maxLength={5}
                  value={customSavCode}
                  onChange={(e) => setCustomSavCode(e.target.value.toUpperCase())}
                />
                <input
                  type="text"
                  placeholder="Symbol (e.g. Fr)"
                  maxLength={5}
                  value={customSavSym}
                  onChange={(e) => setCustomSavSym(e.target.value)}
                />
                <button type="submit" disabled={!customSavCode.trim()}>
                  + Add
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Family Sharing Section */}
      {isLoggedIn && (
        <div className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">
              <i className="fi fi-rr-users-alt"></i>
            </div>
            <div>
              <h4 className="settings-card-title">Family Expense Sharing</h4>
              <p className="settings-card-subtitle">
                Link with family members to sync shared household finances in real-time
              </p>
            </div>
          </div>

          {error && (
            <div className="settings-alert settings-alert-danger">
              <i className="fi fi-rr-exclamation"></i>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="settings-alert settings-alert-success">
              <i className="fi fi-rr-check"></i>
              <span>{success}</span>
            </div>
          )}

          {/* CONNECTION STATUS: LOADING SPINNER OR FINAL DATA */}
          {loadingConnection ? (
            <div className="settings-connection-loading">
              <div
                className="spinner-border"
                role="status"
                style={{
                  width: "28px",
                  height: "28px",
                  borderWidth: "2.5px",
                  color: "var(--accent-coral)",
                }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="settings-loading-text">Loading connection details...</span>
            </div>
          ) : loggedinUserExpense && loggedinUserExpense.length > 0 ? (
            <div className="settings-active-group-card">
              {/* Top Status & Unlink */}
              <div className="settings-group-top">
                <div className="d-flex align-items-center gap-2">
                  <span className="settings-pulse-dot"></span>
                  <span className="settings-conn-title">Active Connection</span>
                </div>
                <button
                  type="button"
                  className="settings-unlink-btn"
                  onClick={handleOpenUnlinkModal}
                  disabled={loading}
                  title="Disconnect family group"
                >
                  <i className="fi fi-rr-cross-circle"></i> Unlink
                </button>
              </div>

              {/* Group Name Display or Inline Edit */}
              <div className="settings-group-name-section">
                {isEditingGroupName ? (
                  <form onSubmit={handleSaveGroupName} className="d-flex gap-2 w-100 mt-2">
                    <div style={{ flex: 1, position: "relative" }}>
                      <input
                        type="text"
                        className="modern-input"
                        value={editGroupNameVal}
                        onChange={(e) => setEditGroupNameVal(e.target.value.slice(0, 15))}
                        placeholder="e.g. Family Budget"
                        maxLength={15}
                        style={{ height: "40px", fontSize: "13px", paddingRight: "46px" }}
                        autoFocus
                      />
                      <span
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "11px",
                          color: "var(--text-muted)",
                          pointerEvents: "none",
                        }}
                      >
                        {editGroupNameVal.length}/15
                      </span>
                    </div>
                    <button
                      type="submit"
                      className="btn-coral-modern"
                      disabled={loading || !editGroupNameVal.trim()}
                      style={{ width: "auto", padding: "0 14px", height: "40px", fontSize: "12px", whiteSpace: "nowrap" }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => {
                        setIsEditingGroupName(false);
                        setEditGroupNameVal(groupName);
                      }}
                      style={{ height: "40px", fontSize: "12px", borderRadius: "12px" }}
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <div className="settings-group-name-header">
                    <div>
                      <div className="settings-group-label">Group Name</div>
                      <div className="settings-group-title">{groupName || "Family Group"}</div>
                    </div>
                    <button
                      type="button"
                      className="settings-edit-group-btn"
                      onClick={() => setIsEditingGroupName(true)}
                      title="Edit group name"
                    >
                      <i className="fi fi-rr-pencil"></i>
                    </button>
                  </div>
                )}
              </div>

              {/* Connected Members List */}
              <div className="settings-members-wrap">
                <div className="settings-members-title">
                  <span>Connected Members</span>
                  <span className="settings-members-count">
                    {connectedMembers.length || 2} members
                  </span>
                </div>

                <div className="settings-members-list">
                  {connectedMembers && connectedMembers.length > 0 ? (
                    connectedMembers.map((member, idx) => (
                      <div key={idx} className="settings-member-card">
                        <div className="settings-member-left">
                          <div className={`settings-member-avatar ${member.isYou ? "is-you" : ""}`}>
                            {(member.name || member.email || "U").charAt(0).toUpperCase()}
                          </div>
                          <div className="settings-member-details">
                            <div className="settings-member-name-row">
                              <span className="settings-member-name">
                                {member.name || member.email.split("@")[0]}
                              </span>
                              {member.isYou && (
                                <span className="settings-member-you-tag">You</span>
                              )}
                            </div>
                            <span className="settings-member-email">{member.email}</span>
                          </div>
                        </div>
                        <span className="settings-member-linked-badge">
                          <i className="fi fi-rr-check"></i> Linked
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="settings-raw-emails">
                      <i className="fi fi-rr-envelope me-1"></i> {loggedinUserExpense}
                    </div>
                  )}
                </div>
              </div>

              {/* Add Another Member toggle */}
              {!showAddAnother ? (
                <button
                  type="button"
                  className="settings-add-another-toggle-btn"
                  onClick={() => setShowAddAnother(true)}
                >
                  <i className="fi fi-rr-plus"></i> Add another member to group
                </button>
              ) : (
                <div className="settings-add-another-box mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--primary-dark)" }}>
                      Add Member Email
                    </span>
                    <button
                      type="button"
                      className="border-0 bg-transparent text-muted p-0"
                      onClick={() => setShowAddAnother(false)}
                      style={{ fontSize: "13px" }}
                    >
                      <i className="fi fi-rr-cross-small"></i>
                    </button>
                  </div>
                  <form onSubmit={handleShare} className="d-flex gap-2">
                    <input
                      type="email"
                      placeholder="member@example.com"
                      value={shareWithEmail}
                      onChange={(e) => setShareWithEmail(e.target.value)}
                      className="modern-input"
                      style={{ height: "42px", fontSize: "13px" }}
                    />
                    <button
                      type="submit"
                      className="btn-coral-modern"
                      disabled={loading || !shareWithEmail.trim()}
                      style={{ width: "auto", padding: "0 16px", whiteSpace: "nowrap", height: "42px", fontSize: "13px" }}
                    >
                      Add
                    </button>
                  </form>
                </div>
              )}
            </div>
          ) : (
            /* NOT CONNECTED YET: Add Connection Form */
            <form onSubmit={handleShare} className="settings-link-form">
              <div className="modern-input-group mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="mb-0">Group Name</label>
                  <small style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                    {inputGroupName.length}/15
                  </small>
                </div>
                <input
                  type="text"
                  placeholder="e.g. Family Budget"
                  value={inputGroupName}
                  onChange={(e) => setInputGroupName(e.target.value.slice(0, 15))}
                  maxLength={15}
                  className="modern-input"
                />
              </div>

              <div className="modern-input-group mb-3">
                <label>Partner / Family Member Email</label>
                <input
                  type="email"
                  placeholder="member@example.com"
                  value={shareWithEmail}
                  onChange={(e) => setShareWithEmail(e.target.value)}
                  className="modern-input"
                  required
                />
              </div>

              <div className="settings-my-email-note mb-3">
                <i className="fi fi-rr-info" style={{ color: "var(--accent-coral)" }}></i>
                <span>
                  Your email (<strong>{email}</strong>) will automatically be joined to this group.
                </span>
              </div>

              <button
                type="submit"
                className="btn-coral-modern"
                disabled={loading || !shareWithEmail.trim()}
              >
                <i className="fi fi-rr-link"></i>
                <span>{loading ? "Linking Group..." : "Link & Create Group"}</span>
              </button>
            </form>
          )}
        </div>
      )}

      {/* Custom Expense Categories */}
      <div className="settings-card">
        <div className="settings-card-header">
          <div className="settings-card-icon">
            <i className="fi fi-rr-tags"></i>
          </div>
          <div>
            <h4 className="settings-card-title">Custom Expense Categories</h4>
            <p className="settings-card-subtitle">
              Add your own custom categories to organize personal & shared expenses
            </p>
          </div>
        </div>

        <form onSubmit={handleAddCategory} className="d-flex gap-2 mb-3">
          <input
            type="text"
            placeholder="e.g. SUBSCRIPTIONS, PETS, GIFTS"
            className="modern-input"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            style={{ height: "44px" }}
          />
          <button
            type="submit"
            className="btn-primary-modern"
            disabled={loading || !newCategory.trim()}
            style={{ width: "auto", padding: "0 18px", whiteSpace: "nowrap", height: "44px" }}
          >
            <i className="fi fi-rr-plus"></i> Add
          </button>
        </form>

        {loading ? (
          <div className="text-center py-3">
            <i className="fi fi-rr-spinner fa-spin" style={{ color: "var(--accent-mauve)" }}></i>
          </div>
        ) : categories.length === 0 ? (
          <p style={{ fontSize: "13px", color: "var(--text-muted)", textAlign: "center", margin: "16px 0" }}>
            No custom expense categories found. Add your first one above!
          </p>
        ) : (
          <div className="d-flex flex-column">
            {categories.map((cat) => (
              <div key={cat.id} className="settings-cat-item">
                <div className="settings-cat-name">
                  <i className="fi fi-rr-bookmark" style={{ color: "var(--accent-coral)" }}></i>
                  <span>{cat.category}</span>
                </div>
                <button
                  type="button"
                  className="settings-delete-btn"
                  onClick={() => deleteCategory(cat.id)}
                  title="Delete category"
                >
                  <i className="fi fi-rr-trash"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task & To-Do Categories */}
      <TodoSettings />

      {/* Themed Unlink Confirmation Bottom Sheet Modal */}
      {showUnlinkModal &&
        ReactDOM.createPortal(
          <div className="bottom-sheet-overlay top-sheet" onClick={closeUnlinkSheet}>
            <div className={`bottom-sheet-content top-sheet${unlinkClosing ? " sheet-closing" : ""}`} onClick={(e) => e.stopPropagation()}>
              <div className="bottom-sheet-handle" onClick={closeUnlinkSheet}></div>
              <button
                type="button"
                className="bottom-sheet-close-corner"
                onClick={closeUnlinkSheet}
                aria-label="Close"
                title="Close"
              >
                <i className="fi fi-rr-cross"></i>
              </button>

              <div className="text-center py-2">
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "rgba(220, 38, 38, 0.12)",
                    color: "#DC2626",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    margin: "0 auto 14px auto",
                  }}
                >
                  <i className="fi fi-rr-cross-circle"></i>
                </div>

                <h4 style={{ fontSize: "18px", fontWeight: 700, color: "var(--primary-dark)", marginBottom: "6px" }}>
                  Disconnect Group
                </h4>
                <p style={{ fontSize: "13px", color: "var(--text-muted)", maxWidth: "320px", margin: "0 auto 16px auto", lineHeight: "1.45" }}>
                  This will disconnect you and all members from this sharing group. Shared expenses will no longer sync between accounts.
                </p>

                <div style={{ maxWidth: "320px", margin: "0 auto 20px auto", textAlign: "left" }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--primary-dark)", marginBottom: "6px", display: "block" }}>
                    Type <strong style={{ color: "#DC2626" }}>confirm</strong> to proceed:
                  </label>
                  <input
                    type="text"
                    className="modern-input w-100"
                    placeholder="Type confirm"
                    value={unlinkConfirmText}
                    onChange={(e) => setUnlinkConfirmText(e.target.value)}
                    style={{
                      borderRadius: "12px",
                      fontSize: "14px",
                      padding: "10px 14px",
                      border:
                        unlinkConfirmText.trim().toLowerCase() === "confirm"
                          ? "1.5px solid #16A34A"
                          : "1.5px solid var(--border-subtle)",
                      background: "#FFFFFF",
                    }}
                    autoFocus
                  />
                </div>

                <div className="d-flex gap-2" style={{ maxWidth: "320px", margin: "0 auto" }}>
                  <button
                    type="button"
                    className="btn btn-light flex-1 py-2"
                    style={{
                      borderRadius: "14px",
                      fontWeight: 600,
                      border: "1px solid var(--border-subtle)",
                      fontSize: "14px",
                    }}
                    onClick={closeUnlinkSheet}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger flex-1 py-2"
                    style={{
                      borderRadius: "14px",
                      fontSize: "14px",
                      fontWeight: 600,
                      background:
                        unlinkConfirmText.trim().toLowerCase() === "confirm"
                          ? "#DC2626"
                          : "rgba(220, 38, 38, 0.4)",
                      borderColor: "transparent",
                      cursor:
                        unlinkConfirmText.trim().toLowerCase() === "confirm" && !loading
                          ? "pointer"
                          : "not-allowed",
                    }}
                    onClick={handleConfirmUnlink}
                    disabled={unlinkConfirmText.trim().toLowerCase() !== "confirm" || loading}
                  >
                    {loading ? "Unlinking..." : "Unlink"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Setting;
